(() => {
  const videoSelector = 'video[data-libaas-autoplay-video]';
  const initializedVideos = new WeakSet();
  const observedVideos = new WeakSet();
  let videoObserver;

  function prepareVideo(video) {
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('autoplay', '');
    video.setAttribute('muted', '');
    video.setAttribute('loop', '');
    video.setAttribute('playsinline', '');
  }

  function loadVideo(video) {
    if (video.dataset.libaasVideoLoaded === 'true') return;

    const source = video.querySelector('source[data-libaas-src]');
    if (!source) {
      video.dataset.libaasVideoLoaded = 'true';
      return;
    }

    source.src = source.dataset.libaasSrc;
    source.removeAttribute('data-libaas-src');
    video.dataset.libaasVideoLoaded = 'true';
    video.preload = 'metadata';
    video.load();
  }

  function playVideo(video) {
    prepareVideo(video);
    loadVideo(video);

    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {
        // A later pageshow or section refresh can retry without affecting other videos.
      });
    }
  }

  function isNearViewport(video) {
    const bounds = video.getBoundingClientRect();
    const margin = 300;
    return bounds.top < window.innerHeight + margin && bounds.bottom > -margin;
  }

  function getObserver() {
    if (videoObserver || !('IntersectionObserver' in window)) return videoObserver;

    videoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          playVideo(entry.target);
          videoObserver.unobserve(entry.target);
        });
      },
      {
        rootMargin: '300px 0px',
        threshold: 0.01,
      }
    );

    return videoObserver;
  }

  function initializeVideo(video) {
    if (initializedVideos.has(video)) return;

    initializedVideos.add(video);
    prepareVideo(video);

    video.addEventListener(
      'loadedmetadata',
      () => {
        playVideo(video);
      },
      { once: true }
    );

    // Above-the-fold and restored-page videos must not wait for the observer.
    if (isNearViewport(video)) {
      playVideo(video);
      return;
    }

    const observer = getObserver();
    if (observer && !observedVideos.has(video)) {
      observedVideos.add(video);
      observer.observe(video);
    }
  }

  function initializeVideos(root = document) {
    if (root.matches && root.matches(videoSelector)) initializeVideo(root);
    root.querySelectorAll(videoSelector).forEach(initializeVideo);
  }

  function resumeVisibleVideos() {
    document.querySelectorAll(videoSelector).forEach((video) => {
      if (isNearViewport(video)) playVideo(video);
    });
  }

  function initializePage() {
    initializeVideos();
    resumeVisibleVideos();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializePage, { once: true });
  } else {
    initializePage();
  }

  window.addEventListener('pageshow', () => {
    initializePage();
  });

  document.addEventListener('shopify:section:load', (event) => {
    initializeVideos(event.target);
    resumeVisibleVideos();
  });
})();